import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Locales - Common
import enCommon from './locales/en/common.json';
import esCommon from './locales/es/common.json';

// Locales - Modules
import enLeads from './locales/en/leads.json';
import esLeads from './locales/es/leads.json';
import enTasks from './locales/en/tasks.json';
import esTasks from './locales/es/tasks.json';
import enProjects from './locales/en/projects.json';
import esProjects from './locales/es/projects.json';
import enInventory from './locales/en/inventory.json';
import esInventory from './locales/es/inventory.json';
import enAccounting from './locales/en/accounting.json';
import esAccounting from './locales/es/accounting.json';
import enExpenses from './locales/en/expenses.json';
import esExpenses from './locales/es/expenses.json';
import enBudget from './locales/en/budget.json';
import esBudget from './locales/es/budget.json';
import enCustomer from './locales/en/customer.json';
import esCustomer from './locales/es/customer.json';
import enDashboard from './locales/en/dashboard.json';
import esDashboard from './locales/es/dashboard.json';
import enLocations from './locales/en/locations.json';
import esLocations from './locales/es/locations.json';
import enOnboarding from './locales/en/onboarding.json';
import esOnboarding from './locales/es/onboarding.json';
import enProfile from './locales/en/profile.json';
import esProfile from './locales/es/profile.json';
import enSchedule from './locales/en/schedule.json';
import esSchedule from './locales/es/schedule.json';
import enSubscription from './locales/en/subscription.json';
import esSubscription from './locales/es/subscription.json';
import enTeam from './locales/en/team.json';
import esTeam from './locales/es/team.json';
import enWorkOrders from './locales/en/workOrders.json';
import esWorkOrders from './locales/es/workOrders.json';
import enPartner from './locales/en/partner.json';
import esPartner from './locales/es/partner.json';
import enWelcomeTour from './locales/en/welcomeTour.json';
import esWelcomeTour from './locales/es/welcomeTour.json';
import enQuotes from './locales/en/quotes.json';
import esQuotes from './locales/es/quotes.json';

// Types
export type SupportedLanguage = 'en' | 'es';

export const SUPPORTED_LANGUAGES: { code: SupportedLanguage; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
];

const STORAGE_KEY = 'nimax_language';

const getStoredLanguage = (): SupportedLanguage => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'es') {
    return stored;
  }
  return 'en';
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        leads: enLeads,
        tasks: enTasks,
        projects: enProjects,
        inventory: enInventory,
        accounting: enAccounting,
        expenses: enExpenses,
        budget: enBudget,
        customer: enCustomer,
        dashboard: enDashboard,
        locations: enLocations,
        onboarding: enOnboarding,
        profile: enProfile,
        schedule: enSchedule,
        subscription: enSubscription,
        team: enTeam,
        workOrders: enWorkOrders,
        partner: enPartner,
        welcomeTour: enWelcomeTour,
        quotes: enQuotes,
      },
      es: {
        common: esCommon,
        leads: esLeads,
        tasks: esTasks,
        projects: esProjects,
        inventory: esInventory,
        accounting: esAccounting,
        expenses: esExpenses,
        budget: esBudget,
        customer: esCustomer,
        dashboard: esDashboard,
        locations: esLocations,
        onboarding: esOnboarding,
        profile: esProfile,
        schedule: esSchedule,
        subscription: esSubscription,
        team: esTeam,
        workOrders: esWorkOrders,
        partner: esPartner,
        welcomeTour: esWelcomeTour,
        quotes: esQuotes,
      },
    },
    lng: getStoredLanguage(),
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: [
      'common',
      'leads',
      'tasks',
      'projects',
      'inventory',
      'accounting',
      'expenses',
      'budget',
      'customer',
      'dashboard',
      'locations',
      'onboarding',
      'profile',
      'schedule',
      'subscription',
      'team',
      'workOrders',
      'partner',
      'welcomeTour',
      'quotes',
    ],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

export const changeLanguage = (lang: SupportedLanguage): void => {
  localStorage.setItem(STORAGE_KEY, lang);
  i18n.changeLanguage(lang);
};

export const getCurrentLanguage = (): SupportedLanguage => {
  return (i18n.language as SupportedLanguage) || 'en';
};

export default i18n;

